import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type {
  ConversationRouterState,
  WriteFromDeepLinkParams,
  WriteFromTemplateParams,
} from "@mirage/service-conversation/router_state_types";
import { CtaSource } from "@mirage/service-conversation/router_state_types";

import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { RouteToSlug } from "@mirage/shared/fss-bridge/bridgeRouting";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";

type RouteToProps = { slug?: string };

export const RouteTo: React.FC<RouteToProps> = ({ slug }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const navigateWithSearch = (
      pathname: RoutePath,
      state?: ConversationRouterState,
    ) => {
      navigate(
        {
          pathname,
          search: searchParams.toString(),
        },
        {
          state,
          replace: true,
        },
      );
    };

    if (!slug) {
      // Redirect to home if no slug provided
      navigateWithSearch(RoutePath.ROOT);
      return;
    }

    switch (slug) {
      case RouteToSlug.WRITE: {
        const routerState = buildConversationRouterState(
          CtaSource.DeepLinkWrite,
          {
            message: i18n.t("assist_templates_type_scratch"),
          } satisfies WriteFromDeepLinkParams,
        );
        navigateWithSearch(RoutePath.CHAT, routerState);
        break;
      }

      case RouteToSlug.WRITE_WITH_TEMPLATE: {
        const templateId = searchParams.get("templateId");
        if (!templateId) {
          // Redirect to basic write if no template ID provided
          const routerState = buildConversationRouterState(
            CtaSource.DeepLinkWrite,
            {
              message: i18n.t("assist_templates_type_scratch"),
            } satisfies WriteFromDeepLinkParams,
          );
          navigateWithSearch(RoutePath.CHAT, routerState);
          break;
        }

        const routerState = buildConversationRouterState(
          CtaSource.DeepLinkWriteWithTemplate,
          {
            templateId,
          } satisfies WriteFromTemplateParams,
        );
        navigateWithSearch(RoutePath.CHAT, routerState);
        break;
      }

      case RouteToSlug.DRAFT: {
        navigateWithSearch(RoutePath.CHAT);
        break;
      }

      case RouteToSlug.HOME: {
        navigateWithSearch(RoutePath.ROOT);
        break;
      }

      default:
        // Unknown slug, redirect to home
        navigateWithSearch(RoutePath.ROOT);
        break;
    }
  }, [slug, searchParams, navigate]);

  // Return null as this component is purely for navigation
  // The user should never see this component render
  return null;
};
